import { useEffect, useState, useRef } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'appState/store'
import { LoadingBox } from '@outbound-ai/ursa'
import { useRouterSetup } from 'hooks'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import { PathsEnum } from 'pages/Paths'
import { ClaimsRouter } from 'pages/Claims'
import { Down } from 'pages/Down'
import { AccountRouter } from './Account/AccountRouter'
import { AutoCheckRouter } from './AutoCheck/AutoCheckRouter'
import { Dashboard } from './Dashboard/dashboard'
import { FileShareRouter } from './FileShare/FileShareRouter'
import { RedirectToTenantUrl } from './RedirectToTenantUrl'
import {
  normalizeClaimDetailPath,
  getDefaultRouteByPermission,
  isRestrictedRoute,
  getTenantIdForRouting,
} from './utils/routerUtils'

interface IRouterAuthProps {
  authenticated: any
  token: string
}

export const Router = ({ authenticated }: IRouterAuthProps) => {
  const { appDown, isLoading } = useRouterSetup()
  const navigate = useNavigate()
  const location = useLocation()

  const [prevRoute, setPrevRoute] = useState<string | undefined>('')
  const [hasRedirectedNoTenants, setHasRedirectedNoTenants] = useState(false)
  const [hasExecutedPermission, setHasExecutedPermission] = useState(false)
  const previousTenantCount = useRef<number>(-1)
  const tenantChangeResetScheduled = useRef(false)

  const { user } = useAppSelector((state) => state.auth)
  const { data: profile, isLoading: isUserProfileLoading } = useUserProfile(user?.id)

  const permissions = profile?.roles?.map((role) => role.roleId) || []
  const tenants = profile?.tenants
  const currentTenantCount = tenants?.length ?? 0

  // Reset redirect flags when user logs out
  useEffect(() => {
    if (!authenticated) {
      setHasRedirectedNoTenants(false)
      setHasExecutedPermission(false)
      previousTenantCount.current = -1
    }
  }, [authenticated])

  // Detect tenant list changes and schedule reset (separate from main routing logic)
  useEffect(() => {
    if (previousTenantCount.current !== -1 &&
      previousTenantCount.current !== currentTenantCount &&
      !tenantChangeResetScheduled.current) {
      tenantChangeResetScheduled.current = true
      // Use setTimeout to batch the state updates and avoid triggering in same render cycle
      setTimeout(() => {
        setHasRedirectedNoTenants(false)
        setHasExecutedPermission(false)
        tenantChangeResetScheduled.current = false
      }, 0)
    }
    previousTenantCount.current = currentTenantCount
  }, [currentTenantCount])

  // Normalize claim detail paths
  useEffect(() => {
    const normalized = normalizeClaimDetailPath(location.pathname)
    if (normalized !== location.pathname) {
      navigate(normalized, { replace: true })
    }
  }, [location.pathname, navigate])

  // Handle user routing based on permissions and tenants
  useEffect(() => {
    // Prevent infinite loops
    if (hasRedirectedNoTenants || hasExecutedPermission) return
    if (isUserProfileLoading || !profile) return

    // Redirect if no tenants available
    if (!tenants || tenants.length === 0) {
      setHasRedirectedNoTenants(true)
      navigate(PathsEnum.home)
      return
    }

    // Don't save prevRoute for routes user doesn't have access to
    const currentRoute = location.pathname
    const hasAccessToCurrentRoute = checkRouteAccess(currentRoute, permissions)

    // Save previous route only if not a restricted path AND user has access to it
    if (!isRestrictedRoute(currentRoute) && hasAccessToCurrentRoute) {
      setPrevRoute(currentRoute)
    }
    // Route to appropriate page based on permissions
    else if (isRestrictedRoute(currentRoute)) {
      routeByPermission()
      setHasExecutedPermission(true)
    }
  }, [
    profile,
    permissions,
    tenants,
    hasExecutedPermission,
    isUserProfileLoading,
    location.pathname,
    hasRedirectedNoTenants,
  ])

  /**
   * Checks if user has access to a specific route based on permissions
   */
  const checkRouteAccess = (pathname: string, userPermissions: string[]): boolean => {
    // Allow access to account, home, and other non-permission routes
    if (pathname.includes('/account') ||
      pathname === PathsEnum.home ||
      pathname.includes('/noaccess-found')) {
      return true
    }

    // Check claims access
    if (pathname.includes('/claims') || pathname.includes('/claim/')) {
      return userPermissions.includes(IUserRoleGuidEnum.claimAccessPermission)
    }

    // Check batch access
    if (pathname.includes('/batch')) {
      return userPermissions.includes(IUserRoleGuidEnum.batchUploadPermission)
    }

    // Check file share access
    if (pathname.includes('/file-share')) {
      return userPermissions.includes(IUserRoleGuidEnum.fileSharePermission)
    }

    return true
  }

  /**
   * Routes user to appropriate page based on their permissions
   */
  const routeByPermission = () => {
    const tntId = getTenantIdForRouting()

    if (!tenants || tenants.length === 0) {
      navigate(PathsEnum.home)
      return
    }

    // Wait for tenant ID to be loaded
    if (!tntId) {
      console.log('[ROUTER] Waiting for tntId to be loaded by TenantSelection')
      return
    }

    const route = getDefaultRouteByPermission(permissions, tntId, prevRoute)
    navigate(route)
  }

  /**
   * Determines the user's default route based on permissions
   */
  const getUserDefaultRoute = (): string => {
    const tntId = getTenantIdForRouting()

    const route = getDefaultRouteByPermission(permissions, tntId, prevRoute)
    // Validate the route before returning
    if (!route || route === PathsEnum.root) {
      return PathsEnum.home
    }
    return route
  }

  // Show loading state
  if (isLoading || isUserProfileLoading) {
    return <LoadingBox size={2} />
  }

  return (
    <RedirectToTenantUrl>
      <Routes>
        {/* App Down page */}
        <Route
          path={PathsEnum.down}
          element={appDown ? <Down /> : <Navigate replace to={getUserDefaultRoute()} />}
        />

        {/* Account routes - with and without tenant for backward compatibility */}
        <Route
          path={`${PathsEnum.account}/${PathsEnum.other}`}
          element={<AccountRouter loggedIn={authenticated} />}
        />
        <Route
          path={`/account/${PathsEnum.other}`}
          element={<AccountRouter loggedIn={authenticated} />}
        />

        {/* Authenticated routes */}
        <Route element={authenticated ? <Outlet /> : <Navigate replace to={PathsEnum.down} />}>
          {/* Claims routes */}
          {permissions?.includes(IUserRoleGuidEnum.claimAccessPermission) && (
            <>
              <Route path={`${PathsEnum.claims}/${PathsEnum.other}`} element={<ClaimsRouter />} />
              <Route path={`/claims/${PathsEnum.other}`} element={<ClaimsRouter />} />
            </>
          )}

          {/* Batch upload routes */}
          {permissions?.includes(IUserRoleGuidEnum.batchUploadPermission) && (
            <>
              <Route path={`${PathsEnum.batch}/${PathsEnum.other}`} element={<AutoCheckRouter />} />
              <Route path={`/batch/${PathsEnum.other}`} element={<AutoCheckRouter />} />
            </>
          )}

          {/* File share routes */}
          {permissions?.includes(IUserRoleGuidEnum.fileSharePermission) && (
            <>
              <Route path={`${PathsEnum.fileShare}/${PathsEnum.other}`} element={<FileShareRouter />} />
              <Route path={`/file-share/${PathsEnum.other}`} element={<FileShareRouter />} />
            </>
          )}

          {/* Home/Dashboard */}
          <Route path={PathsEnum.home} element={<Dashboard />} />
        </Route>

        {/* Catch-all route - redirect to default route based on permissions */}
        <Route path={PathsEnum.other} element={<Navigate replace to={getUserDefaultRoute()} />} />
      </Routes>
    </RedirectToTenantUrl>
  )
}
